import { useCallback, useEffect, useRef, useState } from "react";

export interface IRowStatus {
	state: "saving" | "saved" | "error";
	message?: string;
}

interface IRow {
	id: number | null;
}

const SAVED_TICK_MS = 2000;

/**
 * Rows for an admin table, changed on screen straight away and saved in the
 * background. Each row gets its own status, and a failed save puts the row
 * back the way it was.
 */
export function useSavedRows<T extends IRow>(
	onError: (message: string) => void,
) {
	const [rows, setRowsState] = useState<T[]>();
	const [statuses, setStatuses] = useState<Record<number, IRowStatus>>({});
	const rowsRef = useRef<T[]>();
	const timers = useRef<Record<number, number>>({});
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	useEffect(
		() => () => Object.values(timers.current).forEach(window.clearTimeout),
		[],
	);

	const setRows = useCallback((next: T[]) => {
		rowsRef.current = next;
		setRowsState(next);
	}, []);

	const setStatus = useCallback((ids: number[], status: IRowStatus) => {
		// Cleared here, not in the updater, which React may run after the new timer is set
		ids.forEach(id => window.clearTimeout(timers.current[id]));

		setStatuses(previous => {
			const next = { ...previous };
			ids.forEach(id => {
				next[id] = status;
			});
			return next;
		});

		if (status.state === "saved") {
			ids.forEach(id => {
				timers.current[id] = window.setTimeout(() => {
					setStatuses(previous => {
						const next = { ...previous };
						delete next[id];
						return next;
					});
				}, SAVED_TICK_MS);
			});
		}
	}, []);

	/**
	 * Applies changes to the given rows now, then runs the request. If the
	 * request returns rows, they replace the local copies. If it fails, each
	 * changed field goes back to what it was, unless it has been edited again
	 * since. Resolves to whether the save worked.
	 */
	const save = useCallback(
		async (
			ids: number[],
			changes: Partial<T>,
			request: () => Promise<T[] | void>,
		): Promise<boolean> => {
			const current = rowsRef.current || [];
			const fields = Object.keys(changes) as Array<keyof T>;
			const before: Record<number, Partial<T>> = {};

			setRows(
				current.map(row => {
					if (!ids.includes(row.id as number)) {
						return row;
					}
					before[row.id as number] = pick(row, fields);
					return { ...row, ...changes };
				}),
			);
			setStatus(ids, { state: "saving" });

			try {
				const saved = await request();
				if (saved) {
					const byId = new Map(saved.map(row => [row.id, row]));
					setRows(
						(rowsRef.current || []).map(
							row => byId.get(row.id) || row,
						),
					);
				}
				setStatus(ids, { state: "saved" });
				return true;
			} catch (error) {
				const message =
					error instanceof Error ? error.message : String(error);

				setRows(
					(rowsRef.current || []).map(row => {
						const original = before[row.id as number];
						if (!original) {
							return row;
						}
						const restored = { ...row };
						fields.forEach(field => {
							if (row[field] === changes[field]) {
								restored[field] = original[field] as T[keyof T];
							}
						});
						return restored;
					}),
				);
				setStatus(ids, { state: "error", message });
				onErrorRef.current(message);
				return false;
			}
		},
		[setRows, setStatus],
	);

	return { rows, setRows, statuses, save };
}

function pick<T>(row: T, fields: Array<keyof T>): Partial<T> {
	const picked: Partial<T> = {};
	fields.forEach(field => {
		picked[field] = row[field];
	});
	return picked;
}
